import { CalendarClock, FileText, Mic, PhoneIncoming, PhoneOutgoing, Pencil, Trash2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Textarea } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { callbackStatus, callbackWhen, knownZone } from "@/lib/callbacks";
import { noteTitle, stageChangeText, systemEventText } from "@/lib/contacts";
import { callText, humanize } from "@/lib/lead-card";
import {
  useAddNote, useCallDispositions, useContactNotes, useContactTimeline, useEditNote, useRemoveNote,
} from "@/lib/queries";
import { atLeast, type LeadCallback, type LeadCard, type LeadNote, type LeadTimelineEntry } from "@/lib/types";
import { cn, formatDateTime, relativeTime } from "@/lib/utils";

/**
 * The record's Timeline and Notes tabs (ADR-044).
 *
 * The timeline is one list, newest first, from five tables at once — calls, notes, stage moves,
 * callbacks and the platform's own events — merged by core-api and paged back through `next_before`.
 * The chips narrow it server-side (`kinds=`), so "Notes" on a lead with a thousand calls is one small
 * request. The note composer sits at the top of both tabs: a note is one keystroke away.
 *
 * A callback (ADR-080) sits in the list at the moment it was promised, with where it stands now; the
 * one still to be made is also pinned above everything, on the lead's clock, so nobody opening the
 * record misses a promise.
 *
 * A note typed on the record is the writer's, or a supervisor's, to change; a note that came with an
 * outcome and a handoff's brief are what was said at the time, and core-api refuses to change them —
 * the controls follow it.
 */

const KINDS = [
  { key: "", label: "All" },
  { key: "call", label: "Calls" },
  { key: "note", label: "Notes" },
  { key: "stage", label: "Stage" },
  { key: "callback", label: "Callbacks" },
  { key: "system", label: "System" },
] as const;

/** The outcome labels a campaign uses, for a call's or a note's slug. */
function useOutcomeLabel(contact: LeadCard): (slug: string) => string | undefined {
  const outcomes = useCallDispositions(contact.calling_campaign?.id ?? null);
  return React.useCallback(
    (slug: string) => outcomes.data?.find((d) => d.slug === slug)?.label,
    [outcomes.data],
  );
}

export function NoteComposer({ contact, autoFocus }: { contact: LeadCard; autoFocus?: boolean }) {
  const toast = useToast();
  const add = useAddNote();
  const [body, setBody] = React.useState("");
  const id = React.useId();
  const wrap = React.useRef<HTMLFormElement>(null);

  // `Textarea` takes no ref; the form around it does, and the field is its one textarea.
  React.useEffect(() => {
    if (autoFocus) wrap.current?.querySelector("textarea")?.focus();
  }, [autoFocus]);

  const save = () => {
    const text = body.trim();
    if (!text) return;
    add.mutate(
      { leadId: contact.id, body: text },
      {
        onSuccess: () => {
          setBody("");
          toast.success("Note added");
        },
        onError: (e) =>
          toast.error("Could not add the note", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  };

  return (
    <form
      ref={wrap}
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <label htmlFor={id} className="sr-only">
        Add a note
      </label>
      <Textarea
        id={id}
        rows={2}
        maxLength={4000}
        value={body}
        placeholder="Add a note… (⌘↵ to save)"
        className="min-h-14 font-sans text-sm"
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            save();
          }
        }}
      />
      <div className="flex justify-end">
        <Button type="submit" size="sm" loading={add.isPending} disabled={!body.trim()}>
          Add note
        </Button>
      </div>
    </form>
  );
}

/** One note, with its writer and — for a note typed on the record — Edit and Remove. */
export function NoteItem({
  note, contact, label,
}: {
  note: LeadNote;
  contact: LeadCard;
  label: (slug: string) => string | undefined;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const edit = useEditNote();
  const remove = useRemoveNote();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(note.body);
  const [removing, setRemoving] = React.useState(false);
  const mine = user?.id === note.by_user_id;
  const changeable = note.kind === "note" && (mine || atLeast(user?.role, "supervisor"));
  const edited = note.updated_at !== note.created_at;

  const save = () => {
    const text = draft.trim();
    if (!text || text === note.body) {
      setEditing(false);
      return;
    }
    edit.mutate(
      { leadId: contact.id, noteId: note.id, body: text },
      {
        onSuccess: () => setEditing(false),
        onError: (e) =>
          toast.error("Could not change the note", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  };

  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-xs text-text-muted">
          <span className="font-medium text-text">{noteTitle(note, label)}</span>
          {note.by && ` by ${note.by}`}
          {note.author === "system" && !note.by && " · the platform"}
        </p>
        <span className="flex shrink-0 items-center gap-1">
          {changeable && !editing && (
            <>
              <Button variant="ghost" size="icon" aria-label="Edit note" onClick={() => setEditing(true)}>
                <Pencil />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Remove note" onClick={() => setRemoving(true)}>
                <Trash2 />
              </Button>
            </>
          )}
        </span>
      </div>
      {editing ? (
        <form
          className="mt-1 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Textarea
            autoFocus
            rows={3}
            maxLength={4000}
            value={draft}
            className="font-sans text-sm"
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setDraft(note.body); setEditing(false); }}>
              Cancel
            </Button>
            <Button type="submit" size="sm" loading={edit.isPending} disabled={!draft.trim()}>
              Save
            </Button>
          </div>
        </form>
      ) : (
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-text">{note.body}</p>
      )}
      {edited && !editing && <p className="mt-0.5 text-2xs text-text-subtle">edited {relativeTime(note.updated_at)}</p>}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Remove this note?"
        description="It leaves the record for good. An outcome's note and a handoff's brief cannot be removed."
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { leadId: contact.id, noteId: note.id },
            {
              onSuccess: () => setRemoving(false),
              onError: (e) =>
                toast.error("Could not remove the note", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
    </div>
  );
}

function CallEntry({ entry, label }: { entry: LeadTimelineEntry; label: (slug: string) => string | undefined }) {
  const call = entry.call!;
  const inbound = call.direction === "inbound";
  const Icon = inbound ? PhoneIncoming : PhoneOutgoing;
  const attempts = call.via === "dialer" ? " · dialer" : "";
  return (
    <div className="min-w-0">
      <p className="flex items-center gap-1.5 text-sm text-text">
        <Icon className={cn("size-3.5 shrink-0", inbound ? "text-success-11" : "text-accent-11")} aria-hidden />
        <span className="font-medium">{inbound ? "Inbound call" : "Outbound call"}</span>
        <span className="text-text-muted">· {callText(call, label)}{attempts}</span>
        {call.has_recording && (
          <Tooltip content="Recorded — the Calls tab plays it">
            <Mic className="size-3.5 text-accent-11" aria-label="Recorded" />
          </Tooltip>
        )}
        {call.has_transcript && (
          <Tooltip content="Transcribed — the Calls tab opens it">
            <FileText className="size-3.5 text-success-11" aria-label="Transcribed" />
          </Tooltip>
        )}
      </p>
      <p className="text-xs text-text-muted">
        {[call.campaign_name, call.queue ? `line ${call.queue}` : null].filter(Boolean).join(" · ")}
      </p>
    </div>
  );
}

function StageEntry({ entry }: { entry: LeadTimelineEntry }) {
  const words = stageChangeText(entry.stage!);
  return (
    <p className="text-sm text-text">
      <span className="text-xs font-medium uppercase tracking-wide text-text-subtle">Stage </span>
      {words.title}
      {words.detail && <span className="text-text-muted"> · {words.detail}</span>}
      {entry.by && <span className="text-text-muted"> · {entry.by}</span>}
    </p>
  );
}

/** The lead's clock, as the dialer reads it, when this browser knows the zone. */
function leadZone(contact: LeadCard): string | null {
  return knownZone(contact.clock_timezone ?? contact.timezone);
}

function CallbackEntry({
  callback, contact, label,
}: {
  callback: LeadCallback;
  contact: LeadCard;
  label: (slug: string) => string | undefined;
}) {
  const when = callbackWhen(callback.due_at, leadZone(contact));
  const status = callbackStatus(callback, Date.now());
  const outcome = label(callback.disposition) ?? humanize(callback.disposition) ?? "Callback";
  const then = callback.closed_by ? label(callback.closed_by) ?? humanize(callback.closed_by) : null;
  return (
    <div className="min-w-0">
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
        <CalendarClock className="size-3.5 shrink-0 text-accent-11" aria-hidden />
        <span className="font-medium">{outcome}</span>
        <span className="text-text-muted">· call back {when.there}</span>
        <Badge size="sm" tone={status.tone}>
          {status.label}
          {status.detail && ` · ${status.detail}`}
        </Badge>
      </p>
      <p className="text-xs text-text-muted">
        {[
          when.yours,
          callback.rep && `kept for ${callback.rep}`,
          callback.by && `set by ${callback.by}`,
          !callback.picked && "the outcome's own wait",
          status.label === "Done" && then && `then ${then}`,
        ]
          .filter(Boolean)
          .join(" · ")}
      </p>
    </div>
  );
}

/** The callback still to be made, pinned above the timeline on the lead's clock. */
export function OpenCallback({ contact }: { contact: LeadCard }) {
  const callback = contact.callback;
  if (!callback) return null;
  const when = callbackWhen(callback.due_at, leadZone(contact));
  const status = callbackStatus(callback, Date.now());
  const due = callback.status === "due";
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2.5 rounded-lg border px-3 py-2.5",
        due ? "border-warning-4 bg-warning-2" : "border-accent-7 bg-accent-2",
      )}
    >
      <CalendarClock className={cn("mt-0.5 size-4 shrink-0", due ? "text-warning-11" : "text-accent-11")} aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
          <span className="font-medium">{due ? "Callback due" : "Callback"}</span>
          <span>{when.there}</span>
          <Badge size="sm" tone={status.tone}>
            {status.label}
            {status.detail && ` · ${status.detail}`}
          </Badge>
        </p>
        <p className="text-xs text-text-muted">
          {[when.yours, callback.rep && `kept for ${callback.rep}`, callback.by && `set by ${callback.by}`]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
    </div>
  );
}

function SystemEntry({ entry }: { entry: LeadTimelineEntry }) {
  return (
    <p className="text-sm text-text-muted">
      {systemEventText(entry.event!)}
      <span className="text-text-subtle"> · system</span>
    </p>
  );
}

function When({ at }: { at: string }) {
  return (
    <time dateTime={at} title={formatDateTime(at)} className="shrink-0 whitespace-nowrap text-xs text-text-subtle">
      {relativeTime(at)}
    </time>
  );
}

export function ContactTimeline({ contact, composeFocus }: { contact: LeadCard; composeFocus?: boolean }) {
  const [kind, setKind] = React.useState<string>("");
  const timeline = useContactTimeline(contact.id, kind);
  const label = useOutcomeLabel(contact);
  const entries = timeline.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="space-y-4">
      <OpenCallback contact={contact} />
      <NoteComposer contact={contact} autoFocus={composeFocus} />
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Show">
        {KINDS.map((k) => (
          <button
            key={k.key}
            type="button"
            aria-pressed={kind === k.key}
            onClick={() => setKind(k.key)}
            className={cn(
              "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
              kind === k.key
                ? "border-accent-8 bg-accent-2 text-accent-11"
                : "border-border bg-surface text-text-muted hover:bg-surface-hover",
            )}
          >
            {k.label}
          </button>
        ))}
      </div>
      {timeline.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-4" />
        </div>
      ) : timeline.error ? (
        <ErrorState error={timeline.error} title="Could not load the timeline" />
      ) : entries.length === 0 ? (
        <EmptyState title="Nothing here yet" description="Calls, notes, stage moves, callbacks and the platform's own events appear here as they happen." />
      ) : (
        <ol className="divide-y divide-border">
          {entries.map((entry) => (
            <li key={entry.key} className="flex items-start justify-between gap-4 py-2.5">
              {entry.kind === "call" ? (
                <CallEntry entry={entry} label={label} />
              ) : entry.kind === "note" && entry.note ? (
                <NoteItem note={entry.note} contact={contact} label={label} />
              ) : entry.kind === "stage" ? (
                <StageEntry entry={entry} />
              ) : entry.kind === "callback" && entry.callback ? (
                <CallbackEntry callback={entry.callback} contact={contact} label={label} />
              ) : (
                <SystemEntry entry={entry} />
              )}
              <When at={entry.at} />
            </li>
          ))}
        </ol>
      )}
      {timeline.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" loading={timeline.isFetchingNextPage} onClick={() => void timeline.fetchNextPage()}>
            Earlier
          </Button>
        </div>
      )}
    </div>
  );
}

export function ContactNotes({ contact }: { contact: LeadCard }) {
  const notes = useContactNotes(contact.id);
  const label = useOutcomeLabel(contact);
  return (
    <div className="space-y-4">
      <NoteComposer contact={contact} />
      {notes.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-4" />
        </div>
      ) : notes.error ? (
        <ErrorState error={notes.error} title="Could not load the notes" />
      ) : notes.data.length === 0 ? (
        <EmptyState title="No notes yet" description="What a rep types here, with an outcome, or on a handoff, is kept on the record." />
      ) : (
        <ol className="divide-y divide-border">
          {notes.data.map((note) => (
            <li key={note.id} className="flex items-start justify-between gap-4 py-2.5">
              <NoteItem note={note} contact={contact} label={label} />
              <When at={note.created_at} />
            </li>
          ))}
        </ol>
      )}
      {notes.data && notes.data.some((n) => n.kind !== "note") && (
        <Callout tone="info">A note that came with an outcome, and a handoff's brief, are what was said at the time and cannot be changed.</Callout>
      )}
    </div>
  );
}
